import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Rocket, Loader2, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [blockedMsg, setBlockedMsg] = useState("");
  const [lockMsg, setLockMsg] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const m = localStorage.getItem("bb_blocked_msg");
      if (m) { setBlockedMsg(m); localStorage.removeItem("bb_blocked_msg"); }
    } catch { /* ignore */ }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setBlockedMsg("");
      setLockMsg("");
      login(data.token, data.user, data.parent_unlock);
      toast.success(`Welcome back, ${data.user.name}!`);
      if (data.user.is_admin) navigate("/admin");
      else if (data.user.access_state === "survey_required") navigate("/survey");
      else if (data.user.access_state === "expired") navigate("/access-ended");
      else navigate("/parent");
    } catch (err) {
      const msg = formatError(err.response?.data?.detail) || err.message;
      if (err.response?.status === 403 && msg.includes("contact your administrator")) {
        setBlockedMsg(msg);
      } else if (err.response?.status === 429) {
        setLockMsg(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#EAF7F4] font-parent flex items-center justify-center px-5">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="login-brand">
          <div className="w-10 h-10 rounded-xl bg-blue-600 border-2 border-slate-900 flex items-center justify-center shadow-[3px_3px_0_0_#0F172A]">
            <Rocket color="#fff" size={22} />
          </div>
          <span className="font-kids text-2xl font-bold text-slate-900">Anou Appran</span>
        </Link>
        <div className="nb-card bg-white p-7 sm:p-8">
          <h1 className="font-kids text-2xl font-bold text-slate-900">Welcome back</h1>
          <p className="text-sm text-slate-500 mt-1">Log in to your parent account.</p>
          {blockedMsg && (
            <div data-testid="login-blocked-notice" className="mt-4 flex items-start gap-2 rounded-xl border-2 border-red-500 bg-red-50 p-3 text-sm text-red-700">
              <Ban size={18} className="shrink-0 mt-0.5" />
              <span>{blockedMsg}</span>
            </div>
          )}
          {lockMsg && (
            <div data-testid="login-locked-notice" role="alert" className="mt-4 flex items-start gap-2 rounded-xl border-2 border-amber-500 bg-amber-50 p-3 text-sm text-amber-800">
              <Ban size={18} className="shrink-0 mt-0.5" />
              <span>{lockMsg} <Link to="/forgot-password" className="font-bold underline" data-testid="login-locked-forgot">Forgot password? / Mot de passe oublié ?</Link></span>
            </div>
          )}
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="mt-1.5 h-11 rounded-xl border-2 border-slate-900" placeholder="you@email.com" data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="mt-1.5 h-11 rounded-xl border-2 border-slate-900" placeholder="••••••••" data-testid="login-password" />
              <Link to="/forgot-password" className="mt-1.5 inline-block text-sm font-semibold text-blue-600" data-testid="login-forgot-password">Forgot password? / Mot de passe oublié ?</Link>
            </div>
            <Button type="submit" disabled={loading}
              className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700 border-2 border-slate-900 shadow-[4px_4px_0_0_#0F172A] font-semibold" data-testid="login-submit">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Log in"}
            </Button>
          </form>
          <p className="text-sm text-center text-slate-500 mt-5">
            No account?{" "}
            <Link to="/register" className="font-semibold text-blue-600 hover:underline" data-testid="login-to-register">Sign up</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
